import React, { useState, useEffect } from 'react';
import { 
  Container, Typography, Box, Paper, Button, 
  Grid, Divider, CircularProgress, 
  Alert, AlertTitle, Chip, Tooltip, IconButton,
  List, ListItem, ListItemText, ListItemIcon,
  Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, 
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  PlayArrow as PlayArrowIcon,
  Description as DescriptionIcon,
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  CompareArrows as CompareArrowsIcon
} from '@mui/icons-material';
import axios from 'axios';

// Create a custom API service for test validation
const testValidationService = {
  uploadCodexFiles: async (files) => {
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }
    
    const response = await axios.post('/api/v1/test-validation/upload-codex', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  },
  
  uploadControlDataset: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await axios.post('/api/v1/test-validation/upload-dataset', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  },
  
  runValidation: async () => {
    const response = await axios.post('/api/v1/test-validation/run');
    return response.data;
  },
  
  clearTestData: async () => {
    const response = await axios.delete('/api/v1/test-validation/clear');
    return response.data;
  }
};

// File drop zone component
const FileDropZone = ({ onFilesDrop, acceptedFileTypes, multiple, label }) => {
  const [dragging, setDragging] = useState(false);
  
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragging(true);
  };
  
  const handleDragLeave = () => {
    setDragging(false);
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      onFilesDrop(files);
    }
  };
  
  const handleFileInputChange = (e) => {
    if (e.target.files.length > 0) {
      onFilesDrop(e.target.files);
    }
  };

  return (
    <Box
      sx={{
        border: '2px dashed',
        borderColor: dragging ? 'primary.main' : 'grey.400',
        borderRadius: 2,
        p: 3,
        textAlign: 'center',
        bgcolor: dragging ? 'rgba(0, 0, 0, 0.05)' : 'background.paper',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          bgcolor: 'rgba(0, 0, 0, 0.05)',
        },
      }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => document.getElementById(label).click()}
    >
      <input
        id={label}
        type="file"
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
        accept={acceptedFileTypes}
        multiple={multiple}
      />
      <CloudUploadIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
      <Typography variant="h6" component="div" gutterBottom>
        {label}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Drag and drop files here, or click to select
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        Accepted formats: {acceptedFileTypes.replace(/\./g, '').replace(/,/g, ', ')}
      </Typography>
    </Box>
  );
};

// Main TestValidation component
const TestValidation = () => {
  const [codexFiles, setCodexFiles] = useState([]);
  const [datasetFile, setDatasetFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [uploadStatus, setUploadStatus] = useState({
    codex: false,
    dataset: false
  });

  // Handle codex files upload
  const handleCodexUpload = async (files) => {
    try {
      setLoading(true);
      setError(null);
      
      const fileList = Array.from(files);
      setCodexFiles(prev => [...prev, ...fileList]);
      
      const response = await testValidationService.uploadCodexFiles(fileList);
      
      setUploadStatus(prev => ({
        ...prev,
        codex: true
      }));
      
      setLoading(false);
    } catch (err) {
      setError('Error uploading codex files: ' + (err.response?.data?.message || err.message));
      setLoading(false);
    }
  };

  // Handle dataset file upload
  const handleDatasetUpload = async (files) => {
    try {
      setLoading(true);
      setError(null);
      
      const file = files[0];
      setDatasetFile(file);
      
      const response = await testValidationService.uploadControlDataset(file);
      
      setUploadStatus(prev => ({
        ...prev,
        dataset: true
      }));
      
      setLoading(false);
    } catch (err) {
      setError('Error uploading dataset file: ' + (err.response?.data?.message || err.message));
      setLoading(false);
    }
  };

  // Handle running the validation
  const handleRunValidation = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await testValidationService.runValidation();
      setResults(response);
      
      setLoading(false);
    } catch (err) {
      setError('Error running validation: ' + (err.response?.data?.message || err.message));
      setLoading(false);
    }
  };

  // Handle clearing all test data
  const handleClearTest = async () => {
    try {
      setLoading(true);
      
      await testValidationService.clearTestData();
      
      setCodexFiles([]);
      setDatasetFile(null);
      setResults(null);
      setError(null);
      setUploadStatus({
        codex: false,
        dataset: false
      });
      
      setLoading(false);
    } catch (err) {
      setError('Error clearing test data: ' + (err.response?.data?.message || err.message));
      setLoading(false);
    }
  };

  // Remove a codex file from the list
  const handleRemoveCodexFile = (index) => {
    const newFiles = [...codexFiles];
    newFiles.splice(index, 1);
    setCodexFiles(newFiles);
    
    if (newFiles.length === 0) {
      setUploadStatus(prev => ({
        ...prev,
        codex: false
      }));
    }
  };

  // Remove the dataset file
  const handleRemoveDatasetFile = () => {
    setDatasetFile(null);
    setUploadStatus(prev => ({
      ...prev,
      dataset: false
    }));
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Test Validation
      </Typography>
      
      <Grid container spacing={3}>
        {/* Upload Section */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Upload Test Files
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Upload multiple Codex files (PDF, ZIP) and a control dataset (CSV, XLS) to validate the metadata extraction process.
            </Typography>
            
            <Grid container spacing={2}>
              {/* Codex Files Upload */}
              <Grid item xs={12} md={6}>
                <FileDropZone 
                  onFilesDrop={handleCodexUpload} 
                  acceptedFileTypes=".pdf,.zip" 
                  multiple={true}
                  label="Upload Codex Files"
                />
                
                {codexFiles.length > 0 && (
                  <Paper variant="outlined" sx={{ mt: 2, p: 2, maxHeight: 200, overflow: 'auto' }}>
                    <List dense disablePadding>
                      {codexFiles.map((file, index) => (
                        <ListItem 
                          key={index}
                          secondaryAction={
                            <IconButton edge="end" onClick={() => handleRemoveCodexFile(index)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          }
                        >
                          <ListItemIcon>
                            {file.name.toLowerCase().endsWith('.pdf') ? 
                              <DescriptionIcon fontSize="small" color="error" /> : 
                              <FolderIcon fontSize="small" color="primary" />
                            }
                          </ListItemIcon>
                          <ListItemText 
                            primary={file.name} 
                            secondary={`${(file.size / 1024).toFixed(2)} KB`} 
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                )}
              </Grid>
              
              {/* Dataset File Upload */}
              <Grid item xs={12} md={6}>
                <FileDropZone 
                  onFilesDrop={handleDatasetUpload} 
                  acceptedFileTypes=".csv,.xls,.xlsx" 
                  multiple={false}
                  label="Upload Control Dataset"
                />
                
                {datasetFile && (
                  <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box display="flex" alignItems="center">
                        <FileIcon color="primary" sx={{ mr: 1 }} />
                        <Box>
                          <Typography variant="body2">{datasetFile.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {`${(datasetFile.size / 1024).toFixed(2)} KB`}
                          </Typography>
                        </Box>
                      </Box>
                      <IconButton onClick={handleRemoveDatasetFile}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Paper>
                )}
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
              <Button 
                variant="outlined" 
                color="secondary" 
                startIcon={<DeleteIcon />}
                onClick={handleClearTest}
              >
                Clear All
              </Button>
              
              <Button 
                variant="contained" 
                color="primary" 
                startIcon={<PlayArrowIcon />}
                onClick={handleRunValidation}
                disabled={!uploadStatus.codex || !uploadStatus.dataset || loading}
              >
                Run Validation
              </Button>
            </Box>
          </Paper>
        </Grid>
        
        {/* Results Section */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom display="flex" alignItems="center">
              <CompareArrowsIcon sx={{ mr: 1 }} />
              Validation Results
            </Typography>
            
            {loading && (
              <Box display="flex" flexDirection="column" alignItems="center" py={4}>
                <CircularProgress size={40} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Processing validation...
                </Typography>
              </Box>
            )}
            
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <AlertTitle>Error</AlertTitle>
                {error}
              </Alert>
            )}
            
            {!loading && !error && results && (
              <>
                <Box sx={{ mb: 2, mt: 1 }}>
                  <Grid container spacing={1}>
                    <Grid item>
                      <Chip 
                        label={`Total: ${results.total}`} 
                        color="default" 
                        variant="outlined" 
                      />
                    </Grid>
                    <Grid item>
                      <Chip 
                        label={`Passed: ${results.passed}`} 
                        color="success" 
                        variant="outlined" 
                        icon={<CheckIcon />}
                      />
                    </Grid>
                    <Grid item>
                      <Chip 
                        label={`Failed: ${results.failed}`} 
                        color="error" 
                        variant="outlined" 
                        icon={<CloseIcon />}
                      />
                    </Grid>
                  </Grid>
                </Box>
                
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Codex Name</TableCell>
                        <TableCell>Process ID</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Validation</TableCell>
                        <TableCell>Discrepancies</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {results.validations.map((validation, index) => (
                        <TableRow 
                          key={index}
                          sx={{ 
                            '&:last-child td, &:last-child th': { border: 0 },
                            bgcolor: validation.success ? 'rgba(46, 125, 50, 0.07)' : 'rgba(211, 47, 47, 0.07)'
                          }}
                        >
                          <TableCell component="th" scope="row">
                            {validation.codex.name}
                          </TableCell>
                          <TableCell>{validation.codex.processId}</TableCell>
                          <TableCell>{validation.codex.type}</TableCell>
                          <TableCell>{validation.codex.status}</TableCell>
                          <TableCell align="right">
                            {validation.success ? (
                              <Chip 
                                label="Pass" 
                                color="success" 
                                size="small"
                                icon={<CheckIcon />}
                              />
                            ) : (
                              <Chip 
                                label="Fail" 
                                color="error" 
                                size="small"
                                icon={<CloseIcon />}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            {validation.discrepancies && validation.discrepancies.length > 0 ? (
                              <Tooltip 
                                title={
                                  <Box>
                                    {validation.discrepancies.map((d, idx) => (
                                      <Typography key={idx} variant="caption" display="block">
                                        <strong>{d.field}:</strong> Expected "{d.expected}", got "{d.actual}"
                                      </Typography>
                                    ))}
                                  </Box>
                                }
                              >
                                <Box component="span" sx={{ cursor: 'pointer', textDecoration: 'underline' }}>
                                  {validation.discrepancies.length} issues
                                </Box>
                              </Tooltip>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                None
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
            
            {!loading && !error && !results && (
              <Box display="flex" flexDirection="column" alignItems="center" py={4}>
                <Typography variant="body1" color="text.secondary">
                  No validation results yet. Upload files and run validation to see results.
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default TestValidation; 